import {
  OnboardingAction,
  OnboardingMutationVariables,
  OnboardingUploadMutationVariables,
} from '@chiroup/core/types/Onboarding.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import onboardingService from '../services/onboarding.service';

export const onboardingQueryKey = (clinicId: number) => ['clinic-onboarding', clinicId] as const;

const useOnboarding = (clinicId: number) => {
  const queryClient = useQueryClient();
  const queryKey = onboardingQueryKey(clinicId);
  const query = useQuery(queryKey, () => onboardingService.get(clinicId), {
    enabled: Number.isSafeInteger(clinicId) && clinicId > 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15000,
    retry: false,
  });
  const mutation = useMutation(
    (variables: OnboardingMutationVariables) =>
      onboardingService.act(variables.clinicId, variables.action),
    {
      onMutate: async variables => {
        const key = onboardingQueryKey(variables.clinicId);
        await queryClient.cancelQueries(key, { exact: true });
        return key;
      },
      onSuccess: async (data, variables, key) => {
        const targetKey = key || onboardingQueryKey(variables.clinicId);
        await queryClient.cancelQueries(targetKey, { exact: true });
        queryClient.setQueryData(targetKey, data);
      },
      onSettled: (_data, _error, variables) =>
        queryClient.invalidateQueries(onboardingQueryKey(variables.clinicId), { exact: true }),
    }
  );
  const upload = useMutation(
    (variables: OnboardingUploadMutationVariables<File>) =>
      onboardingService.upload(
        variables.clinicId,
        variables.file,
        variables.category,
        variables.fileId
      ),
    {
      onMutate: async variables => {
        const key = onboardingQueryKey(variables.clinicId);
        await queryClient.cancelQueries(key, { exact: true });
        return key;
      },
      onSuccess: async (data, variables, key) => {
        const targetKey = key || onboardingQueryKey(variables.clinicId);
        await queryClient.cancelQueries(targetKey, { exact: true });
        queryClient.setQueryData(targetKey, data);
      },
      onSettled: (_data, _error, variables) =>
        queryClient.invalidateQueries(onboardingQueryKey(variables.clinicId), { exact: true }),
    }
  );
  return {
    ...query,
    act: (action: OnboardingAction) => mutation.mutateAsync({ clinicId, action }),
    upload: (variables: Omit<OnboardingUploadMutationVariables<File>, 'clinicId'>) =>
      upload.mutateAsync({ ...variables, clinicId }),
    isSaving:
      (mutation.variables?.clinicId === clinicId && mutation.isLoading) ||
      (upload.variables?.clinicId === clinicId && upload.isLoading),
    mutationError:
      (mutation.variables?.clinicId === clinicId ? mutation.error : null) ||
      (upload.variables?.clinicId === clinicId ? upload.error : null),
  };
};

export default useOnboarding;
